import React, { useState, useCallback, useEffect, Fragment } from "react";
import Form from "@rjsf/core";
import validator from "@rjsf/validator-ajv8";
import Dialog from "../dialog";
import InputWithTooltip from "../InputWithTooltip";
import CloseIcon from "../../icons/closeIcon";
import DateInput from "../dateInput";
import { useAxiosPrivate } from "../../hooks/useAxiosPrivate";
import { useAuth } from "../../hooks/useAuth";
import LoadingButton from "../loadingButton";
import ErrorMessage from "../../components/errorMessage";
import styles from "./style.module.css";
import formStyles from "../form/style.module.css";

const AddTrackDayModal = ({
  title = "Enter New Track Day",
  buttonTitle = "Add",
  trackData = {},
  open,
  onClose,
  onAddTrackDay,
}) => {
  const { auth } = useAuth();

  let monitorBy =
    auth?.monitorBy?.charAt(0).toUpperCase() + auth?.monitorBy?.slice(1);

  if (monitorBy === 'Days') {
    monitorBy = 'Track Days';
  } else if (monitorBy === 'Sessions') {
    monitorBy = 'Sessions';
  } else if (monitorBy === 'Laps') {
    monitorBy = 'Miles';
  }

  const schema = {
    type: "object",
    required: ["date", "name", "count"],
    properties: {
      date: {
        title: "Date",
        type: "string",
      },
      name: {
        title: "Track Name",
        type: "string",
      },
      count: {
        title: `# of ${monitorBy}`,
        type: "number",
      },
    },
  };

  const uiSchema = {
    "ui:classNames": formStyles.formInner,
    "ui:rootFieldId": "add_track_day",
    date: {
      "ui:widget": DateInput,
      "ui:classNames": `${formStyles.form_field} ${formStyles.col_12}`,
      "ui:placeholder": "Enter Date",
    },
    name: {
      "ui:classNames": `${formStyles.form_field} ${formStyles.col_6}`,
      "ui:placeholder": "Enter Track Name",
    },
    count: {
      "ui:widget": InputWithTooltip,
      "ui:classNames": `${formStyles.form_field} ${formStyles.col_6}`,
      "ui:placeholder": `Enter # of ${monitorBy}`,
      "ui:options": {
        label: false,
        type: "number",
        tooltipText:
          <Fragment>Note: this field accepts decimals, e.g., if you only run in half the sessions on <strong>Day 2</strong>, you can enter <strong>1.5 days</strong>, and DashBook will adjust wear calculations accordingly.</Fragment>,
        position: ["right"],
        transform: { top: -15, left: 1 },
        minimum: 1,
      },
    },
    "ui:submitButtonOptions": {
      props: {
        className: formStyles.form_btn,
      },
      submitText: "Add",
    },
  };

  const widgets = {
    date: DateInput,
  };

  const axiosPrivate = useAxiosPrivate();
  const [formData, setFormData] = useState({});
  const [id, setID] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const onFormSubmit = (formData) => {
    setFormData(formData);
    if (id) {
      handleUpdateTrackDay(formData);
    } else {
      handleAddTrackDay(formData);
    }
  };

  const handleClose = (changePage = false) => {
    setFormData({});
    onClose(changePage);
  };

  const handleAddTrackDay = useCallback(async (data) => {
    setLoading(true);
    setErrorMessage("");
    try {
      let url = `tracks/add`;
      let options = {
        method: "POST",
        url,
        data,
      };
      await axiosPrivate(options)
        .then((response) => {
          if (response.data.status === 1) {
            handleClose(true);
            onAddTrackDay && onAddTrackDay('add');
          } else {
            handleClose();
          }
          setLoading(false);
        })
        .catch((err) => {
          if (err.response) {
            handleClose();
            setLoading(false);
            setErrorMessage(err.response.data.message);
          }
        });
    } catch (error) {
      handleClose();
      // console.error('error', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdateTrackDay = useCallback(
    async (data) => {
      setLoading(true);
      setErrorMessage("");
      try {
        let url = `tracks/${id}`;
        let options = {
          method: "PUT",
          url,
          data,
        };
        await axiosPrivate(options)
          .then((response) => {
            if (response.data.status === 1) {
              handleClose(true);
              onAddTrackDay && onAddTrackDay('update');
            } else {
              handleClose();
              // console.error('err.res', response);
            }
            setLoading(false);
          })
          .catch((err) => {
            if (err.response) {
              handleClose();
              setLoading(false);
              setErrorMessage(err.response.data.message);
            }
          });
      } catch (error) {
        // console.error('error', error);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [id]
  );

  useEffect(() => {
    if (trackData._id) {
      let tempData = { ...trackData };
      delete tempData._id;
      setID(trackData._id);
      setFormData(tempData);
    }
  }, [trackData]);

  return (
    <Dialog open={open} onClose={() => handleClose(false)} maxWidth="680px">
      <div className={styles.add_service_modal}>
        <div className={styles.add_service_modal_header}>
          <div className={styles.add_service_modal_header_title}>{title}</div>
          <button
            type="button"
            className={styles.add_service_modal_header_close}
            onClick={() => handleClose(false)}
          >
            <CloseIcon />
          </button>
        </div>
        <div className={styles.add_service_modal_content}>
          <Form
            className={formStyles.form}
            schema={schema}
            uiSchema={uiSchema}
            widgets={widgets}
            formData={formData}
            validator={validator}
            showErrorList={false}
            omitExtraData={true}
            liveValidate={false}
            onSubmit={({ formData }) => {
              onFormSubmit(formData);
            }}
          >
            <div className={formStyles.form_actions}>
              <LoadingButton
                type="submit"
                loading={loading}
                title={buttonTitle}
              />
            </div>
          </Form>
          {errorMessage && <ErrorMessage message={errorMessage} mt />}
        </div>
      </div>
    </Dialog>
  );
};

export default AddTrackDayModal;
